import { useState } from "react";
import { User, Mail, Music2, CalendarDays, Link2, ShieldCheck, Eye, Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import api, { formatApiError } from "../../lib/api";

export default function Profile() {
  const { user, refreshUser } = useAuth();
  const [koruma, setKoruma] = useState(() => localStorage.getItem("hoom_koruma") !== "0");
  const [peek, setPeek] = useState(null);
  const [editTiktok, setEditTiktok] = useState(false);
  const [tiktokInput, setTiktokInput] = useState("");
  const [savingTiktok, setSavingTiktok] = useState(false);

  const toggleKoruma = () => {
    const next = !koruma;
    setKoruma(next);
    localStorage.setItem("hoom_koruma", next ? "1" : "0");
    if (next) setPeek(null);
  };

  const tiktokUsername = user?.tiktok_username
    ? user.tiktok_username.startsWith("@")
      ? user.tiktok_username
      : `@${user.tiktok_username}`
    : null;
  const kayit = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
    : null;

  const startEditTiktok = () => {
    setTiktokInput(user?.tiktok_username || "");
    setEditTiktok(true);
  };

  const saveTiktok = async () => {
    if (savingTiktok) return;
    const val = tiktokInput.trim().replace(/^@+/, "");
    if (!val) {
      toast.error("TikTok kullanıcı adı boş olamaz");
      return;
    }
    setSavingTiktok(true);
    try {
      await api.patch("/member/profile", { tiktok_username: val });
      toast.success("TikTok kullanıcı adı güncellendi");
      await refreshUser();
      setEditTiktok(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSavingTiktok(false);
    }
  };

  const Field = ({ id, icon: Icon, label, value, href, onEdit }) => {
    const blurred = koruma && peek !== id;
    const inner = (
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-[#0047FF]/15 border border-cyan-400/20 flex items-center justify-center shrink-0">
          <Icon size={15} strokeWidth={1.5} className="text-cyan-300" />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-[#64748B] font-medium">{label}</p>
          <p
            className={`text-sm font-semibold text-white truncate transition-all ${blurred ? "blur-[5px] select-none" : ""}`}
            data-testid={`profile-field-${id}`}
          >
            {value || "—"}
          </p>
        </div>
      </div>
    );
    const editBtn = onEdit && (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onEdit(); }}
        data-testid={`profile-edit-${id}`}
        aria-label="Düzenle"
        className="absolute top-3 right-3 w-7 h-7 rounded-lg border border-white/10 bg-black/50 flex items-center justify-center text-[#A0ABC0] hover:text-cyan-300 hover:border-cyan-400/40 transition-colors"
      >
        <Pencil size={12} />
      </button>
    );
    if (href && !blurred) {
      return (
        <div className="relative">
          <a href={href} target="_blank" rel="noopener noreferrer" data-testid={`profile-field-link-${id}`}
            className="block rounded-xl border border-white/[0.06] bg-black/30 p-4 hover:border-cyan-400/40 transition-colors">
            {inner}
          </a>
          {editBtn}
        </div>
      );
    }
    return (
      <div className="relative">
        <button type="button" onClick={() => setPeek(blurred ? id : null)} data-testid={`profile-field-btn-${id}`}
          className="block w-full text-left rounded-xl border border-white/[0.06] bg-black/30 p-4 hover:border-cyan-400/40 transition-colors">
          {inner}
        </button>
        {editBtn}
      </div>
    );
  };

  return (
    <div data-testid="member-profile">
      <div className="relative overflow-hidden rounded-2xl border border-white/[0.07] bg-gradient-to-br from-[#0047FF]/15 to-[#00F0FF]/8 bg-[#0A0E17] p-6 md:p-8 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-xs text-cyan-300 font-semibold tracking-wide mb-1.5">HOŞ GELDİN</p>
            <h2 className="font-display font-700 text-xl md:text-2xl text-white" data-testid="profile-welcome">
              {user?.name || "Yayıncı"}
            </h2>
            <p className="text-sm text-[#A0ABC0] mt-2">Hoom Agency yayıncı paneline hoş geldin.</p>
          </div>
          <div className="flex items-center gap-3 bg-black/40 border border-white/10 rounded-full px-4 py-2.5" data-testid="koruma-toggle-wrap">
            <ShieldCheck size={16} strokeWidth={1.5} className="text-cyan-300" />
            <span className="text-xs font-semibold text-white">Yayıncı Koruma Modu</span>
            <button onClick={toggleKoruma} data-testid="koruma-toggle" aria-label="Koruma modu"
              className={`w-11 h-[24px] rounded-full transition-colors relative ${koruma ? "bg-gradient-to-r from-[#0047FF] to-[#00F0FF]" : "bg-white/10"}`}>
              <span className={`absolute top-[3px] w-[18px] h-[18px] rounded-full bg-white transition-all ${koruma ? "left-[24px]" : "left-[3px]"}`} />
            </button>
          </div>
        </div>
        {koruma && (
          <p className="text-[11px] text-[#64748B] mt-4 flex items-center gap-1.5">
            <Eye size={12} /> Bilgiler gizli — görmek için alanın üzerine tıkla veya koruma modunu kapat.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-5">
          <h3 className="text-xs font-bold tracking-wide text-cyan-300 mb-4">KİŞİSEL BİLGİLER</h3>
          <div className="space-y-3">
            <Field id="name" icon={User} label="İsim" value={user?.name} />
            <Field id="email" icon={Mail} label="E-posta Adresi" value={user?.email} />
            {editTiktok ? (
              <div className="rounded-xl border border-cyan-400/40 bg-black/30 p-4" data-testid="profile-tiktok-edit">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-[#0047FF]/15 border border-cyan-400/20 flex items-center justify-center shrink-0">
                    <Music2 size={15} strokeWidth={1.5} className="text-cyan-300" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] text-[#64748B] font-medium mb-1.5">TikTok Adı</p>
                    <input
                      value={tiktokInput}
                      onChange={(e) => setTiktokInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveTiktok()}
                      placeholder="kullaniciadi"
                      data-testid="profile-tiktok-input"
                      className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#64748B] outline-none focus:border-cyan-400/60 transition-colors"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <button onClick={() => setEditTiktok(false)} data-testid="profile-tiktok-cancel"
                    className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-[#A0ABC0] border border-white/10 rounded-full px-3.5 py-1.5 hover:border-white/25 transition-colors">
                    <X size={12} /> Vazgeç
                  </button>
                  <button onClick={saveTiktok} disabled={savingTiktok} data-testid="profile-tiktok-save"
                    className="btn-glow inline-flex items-center gap-1.5 text-[11px] font-semibold text-white rounded-full px-3.5 py-1.5 disabled:opacity-50">
                    <Check size={12} /> {savingTiktok ? "Kaydediliyor..." : "Kaydet"}
                  </button>
                </div>
              </div>
            ) : (
              <Field id="tiktok" icon={Music2} label="TikTok Adı" value={tiktokUsername} onEdit={startEditTiktok} />
            )}
          </div>
        </div>
        <div className="rounded-2xl border border-white/[0.07] bg-[#0A0E17] p-5">
          <h3 className="text-xs font-bold tracking-wide text-cyan-300 mb-4">HESAP DETAYLARI</h3>
          <div className="space-y-3">
            <Field id="kayit" icon={CalendarDays} label="Kayıt Tarihi" value={kayit} />
            <Field id="tiktokurl" icon={Link2} label="TikTok Profili" value={user?.tiktok_url ? "Profili Görüntüle" : null} href={user?.tiktok_url || null} />
          </div>
        </div>
      </div>
    </div>
  );
}
